/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    FormControl,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/*  Helpers */
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const save = async (id, formFields, onSuccessFn) => {
    const {
        identityTenantDomainField,
        identityTenantIdField,
        identityTenantTypeField
    } = formFields;
    try {
        await axios.put(`/tenant/${id}`, {
            global: true,
            identityTenantDomain: identityTenantDomainField,
            identityTenantId: identityTenantIdField,
            identityTenantType: identityTenantTypeField
        });

        Notify.s('Successfully updated tenant.');
        if (onSuccessFn) {
            onSuccessFn();
        }
    } catch (e) {
        debug.e(e.toString());
        Notify.e('Failed to link tenant.');
    }
};

export const LinkAnmTenant = ({ tenant, onSuccessFn }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);
    const {
        id,
        identityTenantDomain,
        identityTenantId,
        identityTenantType,
        partner
    } = tenant;

    const defaultFormFieldState = useMemo(
        () => ({
            identityTenantDomainField: identityTenantDomain || '',
            identityTenantIdField: identityTenantId || '',
            identityTenantTypeField: identityTenantType || ''
        }),
        [identityTenantDomain, identityTenantId, identityTenantType]
    );

    const [formFields, setFormFields] = useState(defaultFormFieldState);

    useEffect(() => {
        // When the user saves their changes, and the tenant data is refetched in the parent component,
        // we should reset the form data using the new tenant object to guarantee it's in sync.

        setFormFields(defaultFormFieldState);
    }, [defaultFormFieldState]);

    if (!partner) {
        return null;
    }
    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            slotProps={{ transition: { unmountOnExit: true } }}
        >
            <AccordionSummary
                expandIcon={
                    <IconWithVariant
                        data-testid="expandAccordionIconLinkAnmTenant"
                        variant="primary"
                        src="ChevronDown"
                        size={FontSizes[2]}
                    />
                }
            >
                <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('GlobalAdmin.Tenant.Edit.LinkAnm.title')}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid item xs={12} mb="16px">
                        <Alert severity="info" icon={false}>
                            {t('GlobalAdmin.Tenant.Edit.LinkAnm.info.1')}
                            <br />
                            <br />
                            {t('GlobalAdmin.Tenant.Edit.LinkAnm.info.2')}
                            <br />
                            <br />
                            {t('GlobalAdmin.Tenant.Edit.LinkAnm.info.3')}:{' '}
                            {tenant.id}
                        </Alert>
                    </Grid>
                    <Grid item xs={12} mb="16px">
                        <FormLabel style={{ fontWeight: 600 }}>
                            {t(
                                'GlobalAdmin.Tenant.Edit.LinkAnm.formFields.tenantID'
                            )}
                        </FormLabel>
                        <TextField
                            onChange={(e) =>
                                setFormFields({
                                    ...formFields,
                                    identityTenantIdField: e.target.value
                                })
                            }
                            value={formFields.identityTenantIdField}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} mb="16px">
                        <FormLabel style={{ fontWeight: 600 }}>
                            {t(
                                'GlobalAdmin.Tenant.Edit.LinkAnm.formFields.tenantDomain'
                            )}
                        </FormLabel>
                        <TextField
                            onChange={(e) =>
                                setFormFields({
                                    ...formFields,
                                    identityTenantDomainField: e.target.value
                                })
                            }
                            value={formFields.identityTenantDomainField}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} mb="16px">
                        <FormLabel
                            style={{
                                display: 'block',
                                fontWeight: 600,
                                marginBottom: '4px'
                            }}
                        >
                            {t(
                                'GlobalAdmin.Tenant.Edit.LinkAnm.formFields.tenantType'
                            )}
                        </FormLabel>
                        <FormControl
                            variant="outlined"
                            style={{ width: '100%' }}
                        >
                            <Select
                                IconComponent={ExpandMore}
                                variant="standard"
                                value={formFields.identityTenantTypeField}
                                onChange={(e) =>
                                    setFormFields({
                                        ...formFields,
                                        identityTenantTypeField: e.target.value
                                    })
                                }
                            >
                                <MenuItem value="ANM">
                                    {t('Common.anm')}
                                </MenuItem>
                                <MenuItem value="">
                                    {t(
                                        'GlobalAdmin.Tenant.Edit.LinkAnm.formFields.tenantType.options.notSet'
                                    )}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <hr />
                    </Grid>

                    <Button
                        data-trackid="globalAdmin_tenant_linkAnmTenant"
                        onClick={() => save(id, formFields, onSuccessFn)}
                        color="primary"
                        variant="contained"
                    >
                        {t('GlobalAdmin.Tenant.Edit.LinkAnm.submitButton')}
                    </Button>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
